/**
 * Zod validation schemas for Product Price types
 * Aligned with API types from @kisanlink/erp-service
 */

import { z } from 'zod';

/**
 * Product Price Response Schema
 * Matches ProductPriceResponse from @kisanlink/erp-service
 */
export const productPriceSchema = z.object({
  id: z.string(),
  variant_id: z.string(),
  price_type: z.string(), // API uses string for flexibility (PURCHASE, SALE, MRP)
  price: z.number().positive('Price must be greater than 0'),
  currency: z.string().default('INR'),
  effective_from: z.string().optional(),
  effective_to: z.string().optional(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

/**
 * Create Product Price Schema
 * Matches CreateProductPriceRequest from @kisanlink/erp-service
 */
export const createProductPriceSchema = z.object({
  variant_id: z.string().min(1, 'Variant ID is required'),
  price_type: z.string().min(1, 'Price type is required'), // PURCHASE, SALE, MRP
  price: z.number().positive('Price must be greater than 0'),
  currency: z.string().optional().default('INR'),
  effective_from: z.string().optional(),
  effective_to: z.string().optional(),
  is_active: z.boolean().optional().default(true),
});

/**
 * Update Product Price Schema
 * Matches UpdateProductPriceRequest from @kisanlink/erp-service
 */
export const updateProductPriceSchema = z.object({
  price: z.number().positive('Price must be greater than 0').optional(),
  price_type: z.string().optional(),
  currency: z.string().optional(),
  effective_from: z.string().optional(),
  effective_to: z.string().optional(),
  is_active: z.boolean().optional(),
});

// Type exports
export type ProductPrice = z.infer<typeof productPriceSchema>;
export type CreateProductPrice = z.infer<typeof createProductPriceSchema>;
export type UpdateProductPrice = z.infer<typeof updateProductPriceSchema>;

// Common price type constants
export const PRICE_TYPES = {
  MSP: 'msp',
  MEMBER: 'member',
  RETAIL: 'retail',
  MRP: 'mrp',
} as const;

export type PriceType = (typeof PRICE_TYPES)[keyof typeof PRICE_TYPES];

// Helper function to get display label for price type
export const getPriceTypeLabel = (priceType: string): string => {
  switch (priceType.toLowerCase()) {
    case 'msp':
      return 'msp (maximum selling price)';
    case 'mrp':
      return 'mrp (maximum retail price)';
    case 'member':
      return 'member';
    case 'retail':
      return 'retail';
    default:
      return priceType;
  }
};
